import { z } from 'zod';
import { cosmosChainSlugSchema, genericAddressSchema, } from './common.js';
const jsonScalarSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
const jsonValueSchema = z.lazy(() => z.union([jsonScalarSchema, z.array(jsonValueSchema), z.record(z.string(), jsonValueSchema)]));
export const denomSchema = z.string().min(1);
export const cosmosHashSchema = z.string().regex(/^(0x)?[0-9a-fA-F]{64}$/, 'Expected 32-byte hexadecimal hash');
export const cosmosCoinAmountSchema = z.object({
    denom: z.string().min(1),
    amount: z.string().regex(/^\d+(\.\d+)?$/),
}).strict();
const paginationSchema = z.object({
    next_key: z.union([z.string(), z.null()]).optional(),
    total: z.string().optional(),
}).catchall(jsonValueSchema);
const cosmosPubKeySchema = z.object({
    '@type': z.string().min(1),
    key: z.string().min(1),
}).catchall(jsonValueSchema);
const cosmosAccountSchema = z.object({
    '@type': z.string().min(1),
    // Direct fields (for BaseAccount types)
    address: genericAddressSchema.optional(),
    pub_key: z.union([cosmosPubKeySchema, z.null()]).optional(),
    account_number: z.string().regex(/^\d+$/).optional(),
    sequence: z.string().regex(/^\d+$/).optional(),
    // Nested structure for vesting accounts (base_vesting_account.base_account)
    // and module accounts (base_account) is captured by catchall
}).catchall(jsonValueSchema);
const cosmosDelegationSchema = z.object({
    delegator_address: genericAddressSchema,
    validator_address: genericAddressSchema,
    shares: z.string().regex(/^\d+(\.\d+)?$/),
}).catchall(jsonValueSchema);
const cosmosDelegationResponseSchema = z.object({
    delegation: cosmosDelegationSchema,
    balance: cosmosCoinAmountSchema,
}).catchall(jsonValueSchema);
const cosmosUnbondingEntrySchema = z.object({
    creation_height: z.string().regex(/^\d+$/).optional(),
    completion_time: z.string().min(1).optional(),
    initial_balance: z.string().regex(/^\d+$/).optional(),
    balance: z.string().regex(/^\d+$/).optional(),
    unbonding_id: z.string().regex(/^\d+$/).optional(),
}).catchall(jsonValueSchema);
const cosmosUnbondingDelegationSchema = z.object({
    delegator_address: genericAddressSchema,
    validator_address: genericAddressSchema,
    entries: z.array(cosmosUnbondingEntrySchema),
}).catchall(jsonValueSchema);
const cosmosStakingRewardSchema = z.object({
    validator_address: genericAddressSchema,
    reward: z.array(cosmosCoinAmountSchema),
}).catchall(jsonValueSchema);
const cosmosValidatorDescriptionSchema = z.object({
    moniker: z.string(),
    identity: z.string(),
    website: z.string(),
    security_contact: z.string(),
    details: z.string(),
}).catchall(jsonValueSchema);
const cosmosValidatorCommissionSchema = z.object({
    commission_rates: z.object({
        rate: z.string().regex(/^\d+(\.\d+)?$/),
        max_rate: z.string().regex(/^\d+(\.\d+)?$/),
        max_change_rate: z.string().regex(/^\d+(\.\d+)?$/),
    }).catchall(jsonValueSchema),
    update_time: z.string().min(1),
}).catchall(jsonValueSchema);
const cosmosValidatorSchema = z.object({
    operator_address: genericAddressSchema,
    consensus_pubkey: cosmosPubKeySchema,
    jailed: z.boolean(),
    status: z.string().min(1),
    tokens: z.string().regex(/^\d+$/),
    delegator_shares: z.string().regex(/^\d+(\.\d+)?$/),
    description: cosmosValidatorDescriptionSchema,
    unbonding_height: z.string().regex(/^\d+$/),
    unbonding_time: z.string().min(1),
    commission: cosmosValidatorCommissionSchema,
    min_self_delegation: z.string().regex(/^\d+$/),
    unbonding_on_hold_ref_count: z.string().regex(/^\d+$/).optional(),
    unbonding_ids: z.array(z.string().regex(/^\d+$/)).optional(),
}).catchall(jsonValueSchema);
const cosmosTxResponseSchema = z.object({
    height: z.string().regex(/^\d+$/),
    txhash: cosmosHashSchema,
    code: z.number().int().nonnegative(),
    raw_log: z.string(),
    gas_wanted: z.string().regex(/^\d+$/),
    gas_used: z.string().regex(/^\d+$/),
    logs: z.array(jsonValueSchema),
    events: z.array(z.object({
        type: z.string().min(1),
        attributes: z.array(z.object({
            key: z.string(),
            value: z.string(),
            index: z.boolean().optional(),
        }).catchall(jsonValueSchema)),
    }).catchall(jsonValueSchema)),
    timestamp: z.string(),
}).catchall(jsonValueSchema);
export const getDenomSupplyParamsSchema = z.object({
    chain: cosmosChainSlugSchema,
    denom: denomSchema,
}).strict();
export const getAccountParamsSchema = z.object({
    chain: cosmosChainSlugSchema,
    address: genericAddressSchema,
}).strict();
export const getDelegationsParamsSchema = z.object({
    chain: cosmosChainSlugSchema,
    delegator: genericAddressSchema,
}).strict();
export const getUnbondingDelegationsParamsSchema = getDelegationsParamsSchema;
export const getStakingRewardsParamsSchema = getDelegationsParamsSchema;
export const getValidatorsParamsSchema = z.object({
    chain: cosmosChainSlugSchema,
}).strict();
export const getValidatorParamsSchema = z.object({
    chain: cosmosChainSlugSchema,
    validatorAddr: genericAddressSchema,
}).strict();
export const getStakingPoolParamsSchema = z.object({
    chain: cosmosChainSlugSchema,
}).strict();
export const getBlockResultsParamsSchema = z.object({
    chain: cosmosChainSlugSchema,
    block: z.number().int().nonnegative(),
}).strict();
export const denomSupplyDataSchema = z.object({
    chain: cosmosChainSlugSchema,
    denom: denomSchema,
    amount: cosmosCoinAmountSchema,
}).strict();
export const cosmosAccountDataSchema = z.object({
    chain: cosmosChainSlugSchema,
    address: genericAddressSchema,
    account: cosmosAccountSchema,
}).strict();
export const cosmosDelegationsDataSchema = z.object({
    chain: cosmosChainSlugSchema,
    delegator: genericAddressSchema,
    delegation_responses: z.array(cosmosDelegationResponseSchema),
    pagination: paginationSchema.optional(),
}).strict();
export const cosmosUnbondingDelegationsDataSchema = z.object({
    chain: cosmosChainSlugSchema,
    delegator: genericAddressSchema,
    unbonding_responses: z.array(cosmosUnbondingDelegationSchema),
    pagination: paginationSchema.optional(),
}).strict();
export const cosmosStakingRewardsDataSchema = z.object({
    chain: cosmosChainSlugSchema,
    delegator: genericAddressSchema,
    rewards: z.array(cosmosStakingRewardSchema),
    total: z.array(cosmosCoinAmountSchema),
}).strict();
export const cosmosValidatorsDataSchema = z.object({
    chain: cosmosChainSlugSchema,
    validators: z.array(cosmosValidatorSchema),
    pagination: paginationSchema.optional(),
}).strict();
export const cosmosValidatorDataSchema = z.object({
    chain: cosmosChainSlugSchema,
    validatorAddr: genericAddressSchema,
    validator: cosmosValidatorSchema,
}).strict();
export const cosmosStakingPoolDataSchema = z.object({
    chain: cosmosChainSlugSchema,
    pool: z.object({
        bonded_tokens: z.string().regex(/^\d+$/),
        not_bonded_tokens: z.string().regex(/^\d+$/),
    }).strict(),
}).strict();
export const cosmosBlockTransactionsDataSchema = z.object({
    chain: cosmosChainSlugSchema,
    blockNumber: z.number().int().nonnegative(),
    hash: z.string().min(1),
    timestamp: z.string().min(1),
    transactionCount: z.number().int().nonnegative(),
    // Decoded `tx_response` objects (sdk 0.50+ LCD path) or raw base64 strings (older chains fallback).
    transactions: z.array(z.union([z.string(), jsonValueSchema])),
}).strict();
export const cosmosTransactionDataSchema = z.object({
    chain: cosmosChainSlugSchema,
    tx: jsonValueSchema.optional(),
    tx_response: cosmosTxResponseSchema.optional(),
}).catchall(jsonValueSchema);
export const cosmosTokenBalanceDataSchema = z.object({
    chain: cosmosChainSlugSchema,
    address: genericAddressSchema,
    denom: denomSchema,
    balance: z.string().regex(/^\d+$/).optional(),
    balanceRaw: z.string().regex(/^\d+$/).optional(),
}).strict();
/** Tendermint block_by_hash response wrapped with `chain` and the input hash. */
export const cosmosBlockByHashDataSchema = z.object({
    chain: cosmosChainSlugSchema,
    hash: z.string().regex(/^0x[0-9A-Fa-f]{64}$/, 'Expected 32-byte 0x-prefixed hex hash'),
    block: jsonValueSchema,
    block_id: jsonValueSchema,
}).strict();
/** Tendermint block_results response wrapped with `chain` and `block` (input height). */
export const cosmosBlockResultsDataSchema = z.object({
    chain: cosmosChainSlugSchema,
    block: z.number().int().nonnegative(),
    results: z.object({
        height: z.string().regex(/^\d+$/),
        txs_results: z.union([z.array(jsonValueSchema), z.null()]),
        finalize_block_events: z.union([z.array(jsonValueSchema), z.null()]).optional(),
        validator_updates: z.union([z.array(jsonValueSchema), z.null()]).optional(),
        consensus_param_updates: jsonValueSchema.optional(),
        app_hash: z.string().optional(),
    }).catchall(jsonValueSchema),
}).strict();
export const cosmosTokenMetadataDataSchema = z.object({
    chain: cosmosChainSlugSchema,
    denom: denomSchema,
    metadata: z.object({
        description: z.string(),
        denom_units: z.array(z.object({
            denom: z.string().min(1),
            exponent: z.number().int().nonnegative(),
            aliases: z.array(z.string()),
        }).catchall(jsonValueSchema)),
        base: z.string().min(1),
        // Some chains' bank modules store an empty display field (e.g. noble uusdc); accept it.
        display: z.string(),
        name: z.string(),
        symbol: z.string(),
        uri: z.string().optional(),
        uri_hash: z.string().optional(),
    }).catchall(jsonValueSchema),
}).strict();
